import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Eye, EyeOff, FileCode2, Image, MonitorPlay, Pencil, Plus, Settings, Trash2 } from 'lucide-react'
import { logoProfilesApi } from '../../lib/api.js'
import type { LogoProfile } from '../../types/index.js'
import { CgTemplateThumbnail } from './CgTemplateThumbnail.js'
import { CgTemplateSettingsDialog, type CgSettingsDraft } from './CgTemplateSettingsDialog.js'

interface Props {
  active?: boolean
  channelId: string
  templatesVersion: number
  onEditTemplate: (templateId: string) => void
  onProfilesChanged: () => void
}

export const CgLogoProfilesPanel = memo(function CgLogoProfilesPanel({ active = true, channelId, templatesVersion, onEditTemplate, onProfilesChanged }: Props) {
  const [profiles, setProfiles] = useState<LogoProfile[]>([])
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [message, setMessage] = useState('')
  const [settingsId, setSettingsId] = useState<string | null>(null)
  const nameInputRef = useRef<HTMLInputElement | null>(null)

  const activeCount = useMemo(() => profiles.filter(profile => profile.active).length, [profiles])
  const settingsProfile = useMemo(() => profiles.find(profile => profile.id === settingsId) ?? null, [profiles, settingsId])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const nextProfiles = await logoProfilesApi.list(channelId, true)
      setProfiles(nextProfiles)
    } catch (error: any) { setMessage(error.message ?? 'Chargement des logos impossible') } finally {
      setLoading(false)
    }
  }, [channelId])

  useEffect(() => { if (active) void load() }, [active, load, templatesVersion])

  // Le nombre de logos n'est pas plafonne : le bouton reste actionnable et dit
  // ce qui manque, au lieu de rester grise sans expliquer pourquoi.
  const createProfile = useCallback(async () => {
    if (busyRef.current) return
    const trimmed = name.trim()
    if (!trimmed) {
      setMessage('Donnez un nom au logo (par exemple « Logo partenaire ») avant de l\u2019ajouter.')
      nameInputRef.current?.focus()
      return
    }
    setMessage('')
    busyRef.current = true; setBusy(true)
    try {
      const profile = await logoProfilesApi.create({ channelId, name: trimmed })
      setName('')
      await load()
      onProfilesChanged()
      if (profile.templateId) onEditTemplate(profile.templateId)
    } catch (error: any) {
      setMessage(error.message ?? 'Creation impossible')
    } finally { busyRef.current = false; setBusy(false) }
  }, [channelId, load, name, onEditTemplate, onProfilesChanged])

  const updateProfile = useCallback(async (id: string, patch: Partial<Pick<LogoProfile, 'name' | 'key' | 'active' | 'manualOnGrid'>> & { expectedRevision?: string }) => {
    if (busyRef.current) return
    busyRef.current = true; setBusy(true); setMessage('')
    try {
      const updated = await logoProfilesApi.update(id, patch)
      setProfiles(current => current.map(profile => profile.id === id ? updated : profile))
      onProfilesChanged()
    } catch (error: any) { setMessage(error.message ?? 'Modification impossible'); throw error }
    finally { busyRef.current = false; setBusy(false) }
  }, [onProfilesChanged])

  const saveSettings = useCallback(async (draft: CgSettingsDraft) => {
    if (!settingsProfile) return
    await updateProfile(settingsProfile.id, { name: draft.name, key: draft.slug, manualOnGrid: draft.manualOnGrid, expectedRevision: draft.expectedRevision })
  }, [settingsProfile, updateProfile])

  const deleteProfile = useCallback(async (profile: LogoProfile) => {
    if (busyRef.current) return
    if (!confirm(`Supprimer le logo "${profile.name}" ?`)) return
    setMessage('')
    busyRef.current = true; setBusy(true)
    try {
      await logoProfilesApi.delete(profile.id)
      await load()
      onProfilesChanged()
    } catch (error: any) {
      setMessage(error.message ?? 'Suppression impossible')
    } finally { busyRef.current = false; setBusy(false) }
  }, [load, onProfilesChanged])

  return (
    <div className="cg-template-home cg-logo-profiles">
      <div className="cg-template-home-toolbar">
        <div className="cg-template-home-summary">
          <Image size={18} />
          <div>
            <strong>Logos antenne</strong>
            <small>
              {profiles.length} logo{profiles.length > 1 ? 's' : ''} · {activeCount} actif{activeCount > 1 ? 's' : ''}
            </small>
          </div>
        </div>
        <div className="cg-logo-profile-create">
          <input
            disabled={busy}
            ref={nameInputRef}
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') { event.preventDefault(); void createProfile() }
            }}
            placeholder="Nom du nouveau logo"
            aria-label="Nom du nouveau logo"
          />
          <button type="button" className="cg-button is-primary" onClick={createProfile} disabled={busy} title="Ajouter un logo antenne">
            <Plus size={14} />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {message && <div className="cg-empty" role="status">{message}</div>}

      <div className="cg-template-tiles">
        {active && profiles.map(profile => (
          <article key={profile.id} className={`cg-template-tile${profile.active ? '' : ' is-muted'}`}>
            <button
              type="button"
              className="cg-template-tile-preview"
              disabled={busy || !profile.templateId}
              onClick={() => profile.templateId && onEditTemplate(profile.templateId)}
              title="Modifier le template du logo"
              aria-label={`Modifier le template de ${profile.name}`}
            >
              <CgTemplateThumbnail template={profile.template} />
            </button>
            <div className="cg-template-tile-body">
              <strong title={profile.name}>{profile.name}</strong>
              <small title={profile.template?.htmlPath ?? undefined}>{profile.key}</small>
            </div>
            <div className="cg-template-tile-badges">
              {profile.manualOnGrid && <span className="cg-template-tile-badge"><MonitorPlay size={10} />Grille</span>}
              {!profile.active && <span className="cg-template-tile-badge">Masqué</span>}
            </div>
            <div className="cg-template-tile-actions">
              <button
                type="button"
                className="cg-icon-button"
                disabled={busy}
                onClick={() => setSettingsId(profile.id)}
                title="Réglages du logo"
                aria-label={`Réglages de ${profile.name}`}
              >
                <Settings size={13} />
              </button>
              <button
                type="button"
                className="cg-icon-button"
                disabled={busy || !profile.templateId}
                onClick={() => profile.templateId && onEditTemplate(profile.templateId)}
                title="Modifier le template du logo"
                aria-label={`Modifier le template de ${profile.name}`}
              >
                <Pencil size={13} />
              </button>
              <button
                type="button"
                className="cg-icon-button"
                disabled={busy}
                onClick={() => void updateProfile(profile.id, { active: !profile.active }).catch(() => undefined)}
                title={profile.active ? 'Masquer ce logo' : 'Activer ce logo'}
                aria-label={profile.active ? `Masquer ${profile.name}` : `Activer ${profile.name}`}
              >
                {profile.active ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
              <button
                type="button"
                className="cg-icon-button is-danger"
                disabled={busy}
                onClick={() => void deleteProfile(profile)}
                title="Supprimer le logo"
                aria-label={`Supprimer ${profile.name}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </article>
        ))}
        {!loading && profiles.length === 0 && (
          <div className="cg-template-home-empty"><FileCode2 size={24} /><span>Aucun logo</span></div>
        )}
      </div>

      {settingsProfile && (
        <CgTemplateSettingsDialog
          title={`Réglages — ${settingsProfile.name}`}
          maxNameLength={120}
          initial={{ expectedRevision: settingsProfile.template?.revision, name: settingsProfile.name, slug: settingsProfile.key, manualOnGrid: settingsProfile.manualOnGrid }}
          htmlPath={settingsProfile.template?.htmlPath}
          cgLayer={null}
          onClose={() => setSettingsId(null)}
          onSubmit={saveSettings}
        />
      )}
    </div>
  )
})
